import {
  LayoutDashboard,
  ClipboardList,
  BarChart3,
  Calendar,
  Heart,
  Wallet,
  Settings,
  LogOut,
  MessageSquare,
  Shield,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import { authClient } from "@/services/auth";

const mainItems = [
  { title: "Dashboard", url: "/app", icon: LayoutDashboard },
  { title: "Accounts", url: "/app/accounts", icon: Wallet },
  { title: "Analytics", url: "/app/analytics", icon: BarChart3 },
  { title: "Planning", url: "/app/planning", icon: ClipboardList },
];

const secondaryItems = [
  { title: "Wellness", url: "/app/wellness", icon: Heart },
  { title: "Recurring", url: "/app/recurring", icon: Calendar },
  { title: "Feedback", url: "/app/feedback", icon: MessageSquare },
];

export function AppSidebar() {
  const { open } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  const isActive = (path: string) => {
    if (path === "/app") return currentPath === "/app";
    return currentPath.startsWith(path);
  };

  const handleLogout = async () => {
    await authClient.signOut();
    navigate("/auth");
  };

  return (
    <Sidebar
      collapsible="icon"
      className="border-r border-border/60 bg-card/40"
    >
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="flex items-center gap-2.5 h-auto py-3 mb-1">
            <span className="flex h-7 w-7 items-center justify-center rounded-xl bg-primary text-primary-foreground font-extrabold text-sm shadow-sm shrink-0">B</span>
            {open && (
              <span className="text-base font-bold tracking-tight text-foreground">
                Budge<span className="text-primary-text">AI</span>
              </span>
            )}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={isActive(item.url)}>
                    <NavLink
                      to={item.url}
                      end={item.url === "/app"}
                      className="flex items-center gap-3 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-lg py-2 hover:bg-muted"
                      activeClassName="font-semibold bg-primary/12 text-primary-text"
                      aria-label={item.title}
                    >
                      <item.icon className="h-4 w-4" aria-hidden="true" />
                      {open && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator className="my-2" />

        <SidebarGroup>
          <SidebarGroupLabel className="text-xs font-medium tracking-wider text-muted-foreground">More</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {secondaryItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={isActive(item.url)}>
                    <NavLink
                      to={item.url}
                      className="flex items-center gap-3 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-lg py-2 hover:bg-muted"
                      activeClassName="font-semibold bg-primary/12 text-primary-text"
                      aria-label={item.title}
                    >
                      <item.icon className="h-4 w-4" aria-hidden="true" />
                      {open && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      
      <SidebarFooter className="border-t border-border/40">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isActive("/app/settings")}>
              <NavLink
                to="/app/settings"
                className="flex items-center gap-2 transition-all duration-200"
                activeClassName="bg-accent text-accent-foreground font-medium"
              >
                <Settings className="h-4 w-4" />
                {open && <span>Settings</span>}
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isActive("/privacy")}>
              <NavLink
                to="/privacy"
                className="flex items-center gap-2 transition-all duration-200"
                activeClassName="bg-accent text-accent-foreground font-medium"
              >
                <Shield className="h-4 w-4" />
                {open && <span>Privacy</span>}
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton 
              onClick={handleLogout} 
              className="flex items-center gap-2 transition-all duration-200 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2 rounded-md"
              aria-label="Logout"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              {open && <span>Logout</span>}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}